import { useQuery } from '@apollo/client';
import CircularProgress from 'components/CircularProgress';
import deviceSizes from 'constants/deviceSizes';
import { useQueryParamsAsKeyValuePair } from 'hooks/useQueryParams';
import useWindowSize from 'hooks/useWindowSize';
import React, { useCallback } from 'react';
import { useHistory } from 'react-router';
import { GET_ACTIVE_APPOINTMENTS_FOR_NOTARY } from 'services/graphql/queries/appointment.query';
import { AppointmentStatus, DocumentStatus, IAppointment } from 'types/appointments';
import { GeneralFunction, OrderEnum } from 'types/common';
import { ITransactionType } from 'types/services';
import { getNewQueryString } from 'utils';
import { IGraphQLResponseData } from 'utils/types';

import DefaultAppointmentListDesign from '../DefaultAppointmentListDesign';
import PaginationComponent from '../PaginationComponent';
import OverdueAppointment from './HistoryAppointment';

const HistoryAppointmentList: React.FC<{
  transactionTypesData?: ITransactionType[];
  refetchAppointmentData?: GeneralFunction;
}> = ({ transactionTypesData }) => {
  const mobileThreshold = deviceSizes.numbers.md;
  const { isMobile } = useWindowSize(mobileThreshold);
  const queryObject = useQueryParamsAsKeyValuePair();
  const history = useHistory();
  const { location } = history;

  const queryVariables = {
    status: AppointmentStatus.COMPLETED,
    documentStatus: DocumentStatus.MAILED,
    order: {
      date: OrderEnum.ASC,
    },
  };

  const { data: appointmentsData, refetch: refetchAppointmentsData, loading: fetchAppointmentsDataLoading } = useQuery<{
    findAppointmentEntity: IGraphQLResponseData<IAppointment>;
  }>(GET_ACTIVE_APPOINTMENTS_FOR_NOTARY, {
    variables: {
      ...queryVariables,
      pagination: { page: parseFloat(queryObject?.page || '1') },
    },
    fetchPolicy: 'cache-and-network',
  });

  const appointments = appointmentsData?.findAppointmentEntity.data;
  const appointmentsMeta = appointmentsData?.findAppointmentEntity.meta;

  const onPageChange = useCallback((page: number) => {
    const newQueryString = getNewQueryString(location.search, { page });
    history.push({
      pathname: location.pathname,
      search: newQueryString,
    });
  }, []);

  return (
    <div className={`notary-appointments-wrapper ${isMobile ? 'mobile' : 'desktop'}`}>
      {fetchAppointmentsDataLoading ? (
        <CircularProgress />
      ) : (
        <div className={`appointments-section overdue ${isMobile ? 'mobile' : 'desktop'}`}>
          <DefaultAppointmentListDesign appointments={appointments}>
            {(app) => (
              <OverdueAppointment
                appointment={app}
                key={app.id}
                transactionTypesData={transactionTypesData}
                refetchAppointmentData={refetchAppointmentsData}
              />
            )}
          </DefaultAppointmentListDesign>
          <PaginationComponent
            page={queryObject?.page}
            loading={fetchAppointmentsDataLoading}
            total={appointmentsMeta?.itemCount}
            onPageChange={onPageChange}
          />
        </div>
      )}
    </div>
  );
};

export default HistoryAppointmentList;
